import {
    getAllowedVehicleList,
    getVehicleInfoByLorryType,
    NEW_LORRY_CAPACITY,
    VehicleInfo
} from "@vahak/core/dist/constants/lorryTypes";

import { generateSelectOption } from "../../../methods/uiUtil";
import { SelectOption } from "@vahak/core-ui/dist/components/Select";
import { getLorryCapacityUnitLabel } from "../../../methods/getLorryCapacityUnitLabel";

function isLorryCompatibleWithCapacity(lorry: VehicleInfo, capacity: number) {
    return capacity >= lorry.capacity.min && capacity <= lorry.capacity.max;
}

export function getLoadQuoteLorryOptionsForCapacity(capacity: number): SelectOption<number>[] {
    if (!Number.isFinite(capacity)) {
        return [];
    }

    return getAllowedVehicleList("load")
        .filter((lorry) => isLorryCompatibleWithCapacity(lorry, capacity))
        .map((lorry) => generateSelectOption(lorry.label, lorry.id as number, lorry.id));
}

function getDefaultCapacityBounds() {
    const configuredBounds = NEW_LORRY_CAPACITY.LOAD_POST;

    if (configuredBounds.max > 0) {
        return configuredBounds;
    }

    const lorries = getAllowedVehicleList("load");
    if (!lorries.length) {
        return configuredBounds;
    }

    return lorries.reduce(
        (range, lorry) => ({
            min: Math.min(range.min, lorry.capacity.min),
            max: Math.max(range.max, lorry.capacity.max)
        }),
        { min: Infinity, max: -Infinity }
    );
}

export function isLoadQuoteCapacityBoundsReady() {
    const { min, max } = getDefaultCapacityBounds();
    return max > min;
}

export function getLoadPostCapacityRangeText(): string | undefined {
    const bounds = NEW_LORRY_CAPACITY.LOAD_POST;

    if (bounds.max > 0) {
        return `${bounds.min}-${bounds.max} T`;
    }

    return undefined;
}

export function getLoadQuoteCapacityRange(lorryTypeId?: number) {
    const vehicle = lorryTypeId ? getVehicleInfoByLorryType(lorryTypeId, "load") : undefined;
    const defaultBounds = getDefaultCapacityBounds();
    const min = vehicle?.capacity?.min ?? defaultBounds.min;
    const max = vehicle?.capacity?.max ?? defaultBounds.max;
    const unit = getLorryCapacityUnitLabel(lorryTypeId);
    const capacityText = vehicle?.capacityText ?? `${min}-${max} ${unit}`;

    return {
        min,
        max,
        capacityText,
        unit
    };
}

export function isLoadQuoteCapacityInRange(capacity: number, lorryTypeId?: number) {
    const { min, max } = getLoadQuoteCapacityRange(lorryTypeId);
    return capacity >= min && capacity <= max;
}

export function parseLoadQuoteCapacity(value?: string): number | undefined {
    if (!value?.trim()) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

/** Numeric capacity used in compact posting (API payload, validation). */
export function getLoadQuoteCapacityNumber(tonnes?: string): number {
    return parseLoadQuoteCapacity(tonnes) ?? 0;
}

export type LoadQuoteCapacityStatus = "empty" | "invalid" | "pending" | "out_of_range" | "unsupported" | "valid";

export function getLoadQuoteCapacityStatus(
    tonnes: string | undefined,
    isRemoteConfigLoaded: boolean
): LoadQuoteCapacityStatus {
    const trimmed = tonnes?.trim() ?? "";

    if (!trimmed) {
        return "empty";
    }

    const capacity = parseLoadQuoteCapacity(trimmed);
    if (capacity == null) {
        return "invalid";
    }

    if (!isRemoteConfigLoaded || !isLoadQuoteCapacityBoundsReady()) {
        return "pending";
    }

    if (!isLoadQuoteCapacityInRange(capacity)) {
        return "out_of_range";
    }

    if (!getLoadQuoteLorryOptionsForCapacity(capacity).length) {
        return "unsupported";
    }

    return "valid";
}
